const SERVER = 'https://vistatech-serversss-production.up.railway.app/api/student';

async function post(action: string, payload: Record<string, unknown>) {
  const res = await fetch(SERVER, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...payload }),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || `${action} failed`);
  return data;
}

export async function apiStudentLogin(email: string, phone: string) {
  return post('studentLogin', { email, phone });
}

export async function apiStudentSetPassword(email: string, phone: string, password: string) {
  return post('studentSetPassword', { email, phone, password });
}

export async function apiStudentVerifyPassword(email: string, password: string) {
  return post('studentVerifyPassword', { email, password });
}

export async function apiGetStudentDashboard(studentId: string) {
  return post('getStudentDashboard', { studentId });
}

export async function apiRaiseComplaint(studentId: string, title: string, description: string) {
  return post('raiseComplaint', { studentId, title, description });
}

export async function apiGetStudentComplaints(studentId: string) {
  return post('getStudentComplaints', { studentId });
}

export async function apiGetJobBoard(studentId: string) {
  return post('getJobBoard', { studentId });
}

export async function apiApplyToJob(studentId: string, jobId: string, screenshotBase64: string, fileName: string) {
  return post('applyToJob', { studentId, jobId, screenshotBase64, fileName });
}

export async function apiUpdateInterviewStatus(studentId: string, jobId: string, interviewStatus: string) {
  return post('studentUpdateInterviewStatus', { studentId, jobId, interviewStatus });
}

// The date the student will make their next fee payment (YYYY-MM-DD) — admin approves it.
export async function apiSubmitPaymentDate(studentId: string, date: string, note: string) {
  return post('submitPaymentDate', { studentId, date, note });
}

// ── My Tasks (server/routes/tasks.js) ──────────────────────────────────────────
// These send the studentToken from sign-in (an older session has none: the
// tab asks to sign in again).
export type TaskStatus = 'Given' | 'Submitted' | 'Accepted' | 'Redo';
export interface MyTaskFile { path: string; name: string; type: string; size: number }
export interface MyTask {
  id: string; taskNo: number; title: string; details: string; givenByName: string; classDate: string;
  dueDate: string; dueLabel: string; status: TaskStatus; overdue: boolean;
  tries: { n: number; at: string; link: string; note: string; late: boolean; files: MyTaskFile[] }[];
  reviews: { n: number; at: string; byName: string; score: number; good: string; improve: string; result: 'Accepted' | 'Redo'; late: boolean; helpful: '' | 'yes' | 'no' }[];
  doubts: { at: string; from: 'student' | 'mentor'; byName: string; text: string }[];
  submittedAt: string | null; score: number | null; reviewedAt: string | null;
}
export interface MyTaskSummary {
  given: number; done: number; waiting: number; toSend: number; redo: number; overdue: number;
  avgScore: number | null; reviewed: number; onTimePct: number | null; redos: number; missed: number;
}

export async function apiStudentTasks(studentToken: string): Promise<{ tasks: MyTask[]; summary: MyTaskSummary; today: string }> {
  return post('studentTasks', { studentToken });
}
export async function apiSubmitTask(studentToken: string, taskId: string, f: { link: string; note: string; files: MyTaskFile[] }): Promise<{ task: MyTask }> {
  return post('studentSubmitTask', { studentToken, taskId, ...f });
}
export async function apiAskDoubt(studentToken: string, taskId: string, text: string): Promise<{ task: MyTask }> {
  return post('studentAskDoubt', { studentToken, taskId, text });
}
export async function apiRateFeedback(studentToken: string, taskId: string, helpful: 'yes' | 'no'): Promise<{ task: MyTask }> {
  return post('studentRateFeedback', { studentToken, taskId, helpful });
}
// A file they sent: a signed link (a path under the API) to open it
export async function apiTaskFileUrl(studentToken: string, taskId: string, path: string): Promise<string> {
  const r = await post('studentTaskFileLink', { studentToken, taskId, path });
  return `${SERVER.replace(/\/student$/, '')}${r.url}`;
}
// A file goes up first (most 10 MB); sending the task then names it. Sent as
// octet-stream so no body parser on the server touches it.
export async function apiUploadTaskFile(studentToken: string, taskId: string, file: File): Promise<MyTaskFile> {
  let res: Response;
  try {
    res = await fetch(`${SERVER.replace(/\/student$/, '')}/task-upload?taskId=${encodeURIComponent(taskId)}&name=${encodeURIComponent(file.name)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/octet-stream', 'X-Student-Token': studentToken }, body: file,
    });
  } catch {
    throw new Error('Upload failed — check your connection');
  }
  const data = await res.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || 'Upload failed');
  return data.file as MyTaskFile;
}
